import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Label, TextArea } from "@/components/ui/Input";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { formatTimestamp } from "@/lib/utils";

interface SettingRow {
  key: string;
  value: unknown;
  notes: string | null;
  updated_at: string;
}

export function Settings() {
  const qc = useQueryClient();
  const hubSettingsAllowed = useHubModuleAdmin("erp");
  const settingsQuery = useQuery({
    queryKey: ["app-settings"],
    queryFn: () => apiFetch<{ settings: SettingRow[] }>("/api/app-settings"),
  });

  const patchMutation = useMutation({
    mutationFn: ({ key, value, notes }: { key: string; value: unknown; notes?: string }) =>
      apiFetch(`/api/app-settings/${encodeURIComponent(key)}`, {
        method: "PATCH",
        body: { value, notes },
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["app-settings"] });
    },
  });

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Hot-reloaded by the scheduler on each tick. Each key stores JSON: booleans, numbers, strings, or nested objects. Examples: true, 30, or a quoted string. Save is per key; invalid JSON shows an error.">
          Global app settings
        </PageTitleWithHelp>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        {settingsQuery.data?.settings.map((setting) => (
          <SettingCard
            key={setting.key}
            setting={setting}
            onSave={(value) => patchMutation.mutate({ key: setting.key, value })}
            saving={patchMutation.isPending}
            hubSettingsAllowed={hubSettingsAllowed}
          />
        ))}
      </div>
    </div>
  );
}

function SettingCard({
  setting,
  onSave,
  saving,
  hubSettingsAllowed,
}: {
  setting: SettingRow;
  onSave: (value: unknown) => void;
  saving: boolean;
  hubSettingsAllowed: boolean;
}) {
  const [draft, setDraft] = useState(JSON.stringify(setting.value, null, 2));
  const [error, setError] = useState<string | null>(null);
  const persistedDraft = JSON.stringify(setting.value, null, 2);
  const isDirty = draft !== persistedDraft;

  useEffect(() => {
    setDraft(persistedDraft);
  }, [persistedDraft]);

  const handleSave = (): void => {
    try {
      const parsed = JSON.parse(draft);
      setError(null);
      onSave(parsed);
    } catch (parseError) {
      setError(`Invalid JSON: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
    }
  };

  return (
    <HubSettingsSection
      title={<span className="font-mono text-sm text-slate-900">{setting.key}</span>}
      hoverHint={setting.notes != null && setting.notes.trim() !== "" ? setting.notes : undefined}
    >
      <Label>Value (JSON)</Label>
      <TextArea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        rows={4}
        disabled={!hubSettingsAllowed}
      />
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">Updated {formatTimestamp(setting.updated_at)}</span>
        <HubSaveButton
          size="sm"
          dirty={isDirty}
          disabled={!hubSettingsAllowed}
          blockedHint="Module administrator access on the Data connectors hub module is required to save global app settings."
          isLoading={saving}
          onClick={handleSave}
        >
          Save
        </HubSaveButton>
      </div>
    </HubSettingsSection>
  );
}
